import React, { useEffect, useState } from "react";
import { Grid, Box, CircularProgress, Typography, Alert } from "@mui/material";
import AssignmentIcon from "@mui/icons-material/Assignment";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import KPICard from "./KPICard";
import BarChartWidget from "../Charts/BarChartWidget";
import PieChartWidget from "../Charts/PieChartWidget";
import LineChartWidget from "../Charts/LineChartWidget";
import {
  getSupportTasksOverview,
  getSupportTicketsOverview,
  getSupportResponseTimes,
  getSupportAgentWorkload,
  DateRangeParams,
} from "../../Services/reportsService";
import { Vocabulary } from "../../Utils/Vocabulary";

interface SupportReportsProps {
  dateRange: DateRangeParams;
  onDataChange?: (data: any) => void;
}

const SupportReports: React.FC<SupportReportsProps> = ({ dateRange, onDataChange }) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [tasksOverview, setTasksOverview] = useState<any>(null);
  const [ticketsOverview, setTicketsOverview] = useState<any>(null);
  const [responseTimes, setResponseTimes] = useState<any>([]);
  const [agentWorkload, setAgentWorkload] = useState<any>([]);

  useEffect(() => {
    fetchSupportData();
  }, [dateRange]);

  const fetchSupportData = async () => {
    setLoading(true);
    setError(null);

    try {
      const [
        tasksOverviewRes,
        ticketsOverviewRes,
        responseTimesRes,
        agentWorkloadRes,
      ] = await Promise.all([
        getSupportTasksOverview(dateRange),
        getSupportTicketsOverview(dateRange),
        getSupportResponseTimes(dateRange),
        getSupportAgentWorkload(dateRange),
      ]);

      const tasksOverviewData = tasksOverviewRes.data;
      const ticketsOverviewData = ticketsOverviewRes.data;
      const responseTimesData = responseTimesRes.data;
      const agentWorkloadData = agentWorkloadRes.data;

      // Normalize shapes
      const normalizedTasksOverview = {
        summary: tasksOverviewData.summary || {},
        tasksByStatus: tasksOverviewData.tasksByStatus || [],
        tasksByPriority: tasksOverviewData.tasksByPriority || [],
      };

      const normalizedTicketsOverview = {
        ticketsByStatus: ticketsOverviewData.ticketsByStatus || [],
        ticketsByType: ticketsOverviewData.ticketsByType || [],
        ticketVolume: ticketsOverviewData.ticketVolume || [],
      };

      const normalizedResponseTimes = Array.isArray(responseTimesData)
        ? responseTimesData.map((r) => ({
          type: r.type || "Unknown",
          averageResponseTime: r.averageResponseTime || 0,
        }))
        : [];

      const normalizedAgentWorkload = Array.isArray(agentWorkloadData)
        ? agentWorkloadData.map((a) => ({
          agentName: a.agentName || a.agentId || "Necunoscut",
          totalTasks: a.totalTasks || 0,
          completionRate: a.completionRate || 0,
        }))
        : [];

      setTasksOverview(normalizedTasksOverview);
      setTicketsOverview(normalizedTicketsOverview);
      setResponseTimes(normalizedResponseTimes);
      setAgentWorkload(normalizedAgentWorkload);

      // Optional: trigger export callback
      if (onDataChange) {
        onDataChange({
          kpis: {
            totalTasks: normalizedTasksOverview.summary.totalTasks || 0,
            completedTasks: normalizedTasksOverview.summary.completedTasks || 0,
            completionRate: normalizedTasksOverview.summary.completionRate || 0,
            averageResponseTime:
              normalizedResponseTimes.reduce((sum, r) => sum + r.averageResponseTime, 0) /
              (normalizedResponseTimes.length || 1),
          },
          tasksByStatus: normalizedTasksOverview.tasksByStatus,
          tasksByPriority: normalizedTasksOverview.tasksByPriority,
          ticketsByStatus: normalizedTicketsOverview.ticketsByStatus,
          ticketsByType: normalizedTicketsOverview.ticketsByType,
          ticketVolume: normalizedTicketsOverview.ticketVolume,
          responseTimes: normalizedResponseTimes,
          agentWorkload: normalizedAgentWorkload,
        });
      }
    } catch (err: any) {
      console.error("Error fetching support data:", err);
      setError(err.message || Vocabulary.errorGeneratingReport);
      if (onDataChange) onDataChange(null);
    } finally {
      setLoading(false);
    }
  };


  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 400 }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>{Vocabulary.loadingReport}</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!tasksOverview || !tasksOverview.summary) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">{Vocabulary.noDataAvailable}</Alert>
      </Box>
    );
  }

  // Calculate average response time
  const avgResponseTime =
    responseTimes.length > 0
      ? (
        responseTimes.reduce((sum, rt) => sum + rt.averageResponseTime, 0) / responseTimes.length
      ).toFixed(2)
      : 0;

  return (
    <Box>
      {/* KPI Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title={Vocabulary.totalTasks}
            value={tasksOverview.summary.totalTasks}
            subtitle={Vocabulary.tasksOverview}
            icon={<AssignmentIcon sx={{ fontSize: 40, color: "#1976d2" }} />}
            color="#1976d2"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title={Vocabulary.completedTasks}
            value={tasksOverview.summary.completedTasks}
            subtitle={Vocabulary.tasksByStatus}
            icon={<CheckCircleIcon sx={{ fontSize: 40, color: "#2e7d32" }} />}
            color="#2e7d32"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title={Vocabulary.completionRate}
            value={`${tasksOverview.summary.completionRate}%`}
            subtitle={Vocabulary.tasksOverview}
            icon={<CheckCircleIcon sx={{ fontSize: 40, color: "#ed6c02" }} />}
            color="#ed6c02"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title={Vocabulary.averageResponseTime}
            value={`${avgResponseTime}h`}
            subtitle={Vocabulary.responseTimes}
            icon={<AccessTimeIcon sx={{ fontSize: 40, color: "#9c27b0" }} />}
            color="#9c27b0"
          />
        </Grid>
      </Grid>

      {/* Charts */}
      <Grid container spacing={3}>
        {/* Tasks by Status */}
        <Grid item xs={12} lg={6}>
          <BarChartWidget
            title={Vocabulary.tasksByStatus}
            data={tasksOverview.tasksByStatus}
            xKey="status"
            yKey="count"
            yLabel={Vocabulary.count}
            color="#1976d2"
            height={350}
          />
        </Grid>

        {/* Tasks by Priority */}
        <Grid item xs={12} lg={6}>
          <PieChartWidget
            title={Vocabulary.tasksByPriority}
            data={tasksOverview.tasksByPriority}
            nameKey="priority"
            valueKey="count"
            height={350}
          />
        </Grid>

        {/* Tickets by Status */}
        <Grid item xs={12} lg={6}>
          <BarChartWidget
            title={Vocabulary.ticketsByStatus}
            data={ticketsOverview?.ticketsByStatus || []}
            xKey="status"
            yKey="count"
            yLabel={Vocabulary.count}
            color="#2e7d32"
            height={350}
          />
        </Grid>

        {/* Tickets by Type */}
        <Grid item xs={12} lg={6}>
          <PieChartWidget
            title={Vocabulary.ticketsByType}
            data={ticketsOverview?.ticketsByType || []}
            nameKey="type"
            valueKey="count"
            height={350}
          />
        </Grid>

        {/* Ticket Volume Over Time */}
        <Grid item xs={12} lg={8}>
          <LineChartWidget
            title="Volum Tichete în Timp"
            data={ticketsOverview?.ticketVolume || []}
            xKey="date"
            yKey="count"
            yLabel={Vocabulary.count}
            color="#ed6c02"
            height={350}
          />
        </Grid>

        {/* Response Times by Type */}
        <Grid item xs={12} lg={4}>
          <BarChartWidget
            title={Vocabulary.responseTimes}
            data={responseTimes}
            xKey="type"
            yKey="averageResponseTime"
            yLabel="Ore"
            color="#9c27b0"
            height={350}
            layout="vertical"
          />
        </Grid>

        {/* Agent Workload - Total Tasks */}
        <Grid item xs={12}>
          <BarChartWidget
            title={Vocabulary.agentWorkload}
            data={agentWorkload}
            xKey="agentName"
            yKey="totalTasks"
            yLabel={Vocabulary.totalTasks}
            color="#1976d2"
            height={350}
          />
        </Grid>

        {/* Agent Completion Rate */}
        <Grid item xs={12}>
          <BarChartWidget
            title="Rată de Finalizare Agenți"
            data={agentWorkload}
            xKey="agentName"
            yKey="completionRate"
            yLabel={Vocabulary.completionRate}
            color="#2e7d32"
            height={350}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default SupportReports;
